/**
 * The waitlist (spec docs/superpowers/specs/2026-10-17-waitlist-front-door-design.md): every new
 * sign-up's front door until the new product ships. Bash asks four tap questions; these are the ids
 * the screen sends and the server keeps. Their words live in apps/web/src/components/waitlist/data.ts.
 */
import { z } from 'zod';

export const WAITLIST_JOB_IDS = ['deals', 'posts', 'research', 'money', 'support', 'code', 'inbox', 'other'] as const;
export const WAITLIST_TOOL_IDS = [
  'gmail', 'googlecalendar', 'slack', 'whatsapp', 'notion', 'hubspot',
  'googledrive', 'shopify', 'xero', 'salesforce', 'linear', 'other',
] as const;
export const WAITLIST_WHO_IDS = ['personal', 'solo', 'smb', 'startup', 'enterprise', 'dev'] as const;
export const WAITLIST_WHEN_IDS = ['week', 'month', 'look'] as const;

const unique = <T>(ids: T[]): T[] => [...new Set(ids)];

export const waitlistAnswersSchema = z.strictObject({
  jobs: z.array(z.enum(WAITLIST_JOB_IDS)).max(16).default([]).transform(unique),
  other: z.string().max(200).default(''),
  tools: z.array(z.enum(WAITLIST_TOOL_IDS)).max(24).default([]).transform(unique),
  who: z.union([z.enum(WAITLIST_WHO_IDS), z.literal('')]).default(''),
  when: z.union([z.enum(WAITLIST_WHEN_IDS), z.literal('')]).default(''),
  note: z.string().max(500).default(''),
});

export type WaitlistAnswers = z.infer<typeof waitlistAnswersSchema>;

export const EMPTY_WAITLIST_ANSWERS: WaitlistAnswers = {
  jobs: [],
  other: '',
  tools: [],
  who: '',
  when: '',
  note: '',
};

/** GET/PUT /api/beta-waitlist/me. position = rows that joined at or before this one and are not let in yet. */
export interface MyWaitlist {
  requested: boolean;
  requestedAt: string | null;
  answers: WaitlistAnswers;
  callBookedAt: string | null;
  position: number | null;
}

/** One row of GET /api/admin/waitlist. */
export interface AdminWaitlistRow {
  email: string;
  name: string | null;
  avatarUrl: string | null;
  requestedAt: string;
  answers: WaitlistAnswers;
  callBookedAt: string | null;
  letInAt: string | null;
}
